import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete, Edit, Logout } from '@mui/icons-material';
import { useTrainings, useSaveTraining, useDeleteTraining } from '../api/trainingsApi';
import { useNews, useSaveNews, useDeleteNews, useReorderNews } from '../api/newsApi';
import {
  useAdminRoles,
  useAdminUsers,
  useAssignAdminUserRole,
  useDeleteAdminRole,
  useDeleteAdminUser,
  useSaveAdminRole,
  useSaveAdminUser,
} from '../api/adminApi';
import { Login } from '../Komponenten/Login';
import './Admin.css';

export function Admin() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({team: '', trainings: '', contact: '', pos: ''});
  const [section, setSection] = useState('trainings');
  const [newsDialogOpen, setNewsDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState({ title: '', date: '', excerpt: '', link: '', image: null, pdf: null, clear_pdf: false });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({ username: '', password: '', role_id: '' });
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', permissions: [] });
  const [user, setUser] = useState(null);

  const { data, isLoading, error } = useTrainings();
  const { data: newsItems = [], isLoading: isNewsLoading, error: newsError } = useNews();
  const trainings = data || [{team: "Beispielteam", trainings: ["Montag 18:00-20:00", "Mittwoch 18:00-20:00"], contact: "Beispielkontakt", pos: 0}];
  const saveMutation = useSaveTraining();
  const deleteMutation = useDeleteTraining();
  const saveNewsMutation = useSaveNews();
  const deleteNewsMutation = useDeleteNews();
  const reorderNewsMutation = useReorderNews();
  const isAdmin = Boolean(user?.is_admin);
  const permissions = user?.permissions || [];
  const canManageTrainings = isAdmin || permissions.includes('manage_trainings');
  const canManageNews = isAdmin || permissions.includes('manage_news');
  const { data: adminUsers = [], isLoading: isUsersLoading, error: usersError } = useAdminUsers(isAdmin);
  const { data: roles = [], isLoading: isRolesLoading, error: rolesError } = useAdminRoles(isAdmin);
  const saveUserMutation = useSaveAdminUser();
  const deleteUserMutation = useDeleteAdminUser();
  const assignUserRoleMutation = useAssignAdminUserRole();
  const saveRoleMutation = useSaveAdminRole();
  const deleteRoleMutation = useDeleteAdminRole();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('https://api.djkdurlach.de/index.php/auth/verify', {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setSection(data.user.is_admin || data.user.permissions?.includes('manage_trainings') ? 'trainings' : 'news');
        }
      } catch (err) {
        // Nicht eingeloggt
      }
    };
    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setSection(userData.is_admin || userData.permissions?.includes('manage_trainings') ? 'trainings' : 'news');
  };

  const handleLogout = async () => {
    await fetch('https://api.djkdurlach.de/index.php/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
    setUser(null);
  };

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const handleOpen = (training = null) => {
    if (training) {
      setEditing(training);
      setForm({
        team: training.team,
        trainings: training.trainings.join('\n'),
        contact: training.contact || '',
        pos: training.pos != null ? String(training.pos) : '',
      });
    } else {
      setEditing(null);
      setForm({ team: '', trainings: '', contact: '', pos: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setForm({ team: '', trainings: '', contact: '', pos: '' });
  };

  const handleSubmit = () => {
    const trainingData = {
      ...form,
      trainings: form.trainings.split('\n').filter(line => line.trim()),
      pos: form.pos !== '' ? Number(form.pos) : 0,
    };
    if (editing) {
      trainingData.id = editing.id;
    }
    saveMutation.mutate(trainingData, {
      onSuccess: () => {
        handleClose();
      },
    });
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleReorderNews = (index, direction) => {
    const reordered = [...newsItems];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= reordered.length) return;
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    reorderNewsMutation.mutate(reordered.map((item) => item.id));
  };

  const handleOpenNews = (news = null) => {
    setEditingNews(news);
    setNewsForm(news
      ? { title: news.title, date: news.date, excerpt: news.excerpt, link: news.link || '', image: null, pdf: null, clear_pdf: false }
      : { title: '', date: '', excerpt: '', link: '', image: null, pdf: null, clear_pdf: false });
    setNewsDialogOpen(true);
  };

  const handleCloseNews = () => {
    setNewsDialogOpen(false);
    setEditingNews(null);
    setNewsForm({ title: '', date: '', excerpt: '', link: '', image: null, pdf: null, clear_pdf: false });
  };

  const handleSubmitNews = (event) => {
    event.preventDefault();
    const newsData = { ...newsForm };
    if (editingNews) {
      newsData.id = editingNews.id;
    }
    saveNewsMutation.mutate(newsData, {
      onSuccess: handleCloseNews,
    });
  };

  const handleSubmitUser = (event) => {
    event.preventDefault();
    saveUserMutation.mutate(userForm, {
      onSuccess: () => {
        setUserDialogOpen(false);
        setUserForm({ username: '', password: '', role_id: '' });
      },
    });
  };

  const handleOpenRole = (role = null) => {
    setEditingRole(role);
    setRoleForm(role ? { name: role.name, permissions: role.permissions } : { name: '', permissions: [] });
    setRoleDialogOpen(true);
  };

  const handleSubmitRole = (event) => {
    event.preventDefault();
    saveRoleMutation.mutate(
      { ...roleForm, ...(editingRole ? { id: editingRole.id } : {}) },
      { onSuccess: () => setRoleDialogOpen(false) },
    );
  };

  const updateRolePermission = (permission, enabled) => {
    const permissions = enabled
      ? [...roleForm.permissions, permission]
      : roleForm.permissions.filter((item) => item !== permission);
    setRoleForm({ ...roleForm, permissions });
  };

  if (!user || (isLoading && !canManageNews && !canManageTrainings)) return !user ? <Login onLogin={handleLogin} /> : <CircularProgress />;

  return (
    <div className="admin-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="admin-title">Admin - Inhalte verwalten</h1>
        <Button
          variant="outlined"
          startIcon={<Logout />}
          onClick={handleLogout}
          style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
        >
          Abmelden
        </Button>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {canManageTrainings && <Button variant={section === 'trainings' ? 'contained' : 'outlined'} onClick={() => setSection('trainings')}>Trainingszeiten</Button>}
        {canManageNews && <Button variant={section === 'news' ? 'contained' : 'outlined'} onClick={() => setSection('news')}>News</Button>}
        {isAdmin && <Button variant={section === 'users' ? 'contained' : 'outlined'} onClick={() => setSection('users')}>Benutzer</Button>}
        {isAdmin && <Button variant={section === 'roles' ? 'contained' : 'outlined'} onClick={() => setSection('roles')}>Rollen</Button>}
      </div>
      {!canManageTrainings && !canManageNews && !isAdmin && <Alert severity="info">Deinem Konto sind noch keine Admin-Berechtigungen zugewiesen.</Alert>}
      {canManageTrainings && section === 'trainings' && <>
      {error && <Alert severity="error" style={{ marginBottom: 20 }}>Fehler beim Laden der Trainings: {error.message}</Alert>}
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => handleOpen()}
        className="admin-add-button"
      >
        Neues Training hinzufügen
      </Button>
      <TableContainer component={Paper} className="admin-table-container">
        <Table>
          <TableHead className="admin-table-head">
            <TableRow>
              <TableCell>Position</TableCell>
              <TableCell>Mannschaft</TableCell>
              <TableCell>Trainingszeiten</TableCell>
              <TableCell>Kontakt</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="admin-table-body">
            {trainings.map((training) => (
              <TableRow key={training.id}>
                <TableCell>{training.pos != null ? training.pos : ''}</TableCell>
                <TableCell>{training.team}</TableCell>
                <TableCell>{training.trainings.join(', ')}</TableCell>
                <TableCell>{training.contact}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(training)} className="admin-edit-icon">
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(training.id)} className="admin-delete-icon">
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} className="admin-dialog">
        <DialogTitle className="admin-dialog-title">{editing ? 'Training bearbeiten' : 'Neues Training'}</DialogTitle>
        <DialogContent className="admin-dialog-content">
          <TextField
            autoFocus
            margin="dense"
            label="Mannschaft"
            fullWidth
            value={form.team}
            onChange={(e) => setForm({ ...form, team: e.target.value })}
            variant="outlined"
          />
          <TextField
            margin="dense"
            label="Trainingszeiten (eine pro Zeile)"
            fullWidth
            multiline
            rows={4}
            value={form.trainings}
            onChange={(e) => setForm({ ...form, trainings: e.target.value })}
            variant="outlined"
          />
          <TextField
            margin="dense"
            label="Position"
            type="number"
            fullWidth
            value={form.pos}
            onChange={(e) => setForm({ ...form, pos: e.target.value })}
            variant="outlined"
          />
          <TextField
            margin="dense"
            label="Kontakt"
            fullWidth
            value={form.contact}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions className="admin-dialog-actions">
          <Button onClick={handleClose} className="admin-cancel-button">Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={saveMutation.isPending} variant="contained" className="admin-save-button">
            {saveMutation.isPending ? <CircularProgress size={20} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
      </>}

      {canManageNews && section === 'news' && <>
        {newsError && <Alert severity="error" style={{ marginBottom: 20 }}>{newsError.message}</Alert>}
        {saveNewsMutation.isError && <Alert severity="error" style={{ marginBottom: 20 }}>{saveNewsMutation.error.message}</Alert>}
        {deleteNewsMutation.isError && <Alert severity="error" style={{ marginBottom: 20 }}>{deleteNewsMutation.error.message}</Alert>}
        {reorderNewsMutation.isError && <Alert severity="error" style={{ marginBottom: 20 }}>{reorderNewsMutation.error.message}</Alert>}
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenNews()} className="admin-add-button">
          Neue News hinzufügen
        </Button>
        {isNewsLoading ? <CircularProgress /> : (
          <TableContainer component={Paper} className="admin-table-container">
            <Table>
              <TableHead className="admin-table-head">
                <TableRow>
                  <TableCell>Bild</TableCell>
                  <TableCell>Titel</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Teaser</TableCell>
                  <TableCell>Reihenfolge</TableCell>
                  <TableCell>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className="admin-table-body">
                {newsItems.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.image && <img src={item.image} alt="" style={{ width: 64, height: 48, objectFit: 'cover' }} />}</TableCell>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>{item.date}</TableCell>
                    <TableCell>{item.excerpt}</TableCell>
                    <TableCell>
                      <IconButton
                        disabled={index === 0 || reorderNewsMutation.isPending}
                        onClick={() => handleReorderNews(index, -1)}
                        aria-label="News nach oben verschieben"
                      >
                        <ArrowUpward />
                      </IconButton>
                      <IconButton
                        disabled={index === newsItems.length - 1 || reorderNewsMutation.isPending}
                        onClick={() => handleReorderNews(index, 1)}
                        aria-label="News nach unten verschieben"
                      >
                        <ArrowDownward />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenNews(item)} className="admin-edit-icon" aria-label="News bearbeiten"><Edit /></IconButton>
                      <IconButton onClick={() => deleteNewsMutation.mutate(item.id)} className="admin-delete-icon" aria-label="News löschen"><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Dialog open={newsDialogOpen} onClose={handleCloseNews} className="admin-dialog" fullWidth>
          <form onSubmit={handleSubmitNews}>
            <DialogTitle className="admin-dialog-title">{editingNews ? 'News bearbeiten' : 'Neue News'}</DialogTitle>
            <DialogContent className="admin-dialog-content">
              <TextField
                autoFocus
                required
                margin="dense"
                label="Titel"
                fullWidth
                value={newsForm.title}
                onChange={(event) => setNewsForm({ ...newsForm, title: event.target.value })}
              />
              <TextField
                required
                margin="dense"
                label="Datum"
                type="date"
                fullWidth
                value={newsForm.date}
                onChange={(event) => setNewsForm({ ...newsForm, date: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                required
                margin="dense"
                label="Teasertext"
                fullWidth
                multiline
                minRows={3}
                value={newsForm.excerpt}
                onChange={(event) => setNewsForm({ ...newsForm, excerpt: event.target.value })}
              />
              <TextField
                margin="dense"
                label="Externer Link (optional; alternativ PDF)"
                type="url"
                fullWidth
                value={newsForm.link}
                onChange={(event) => {
                  const link = event.target.value;
                  setNewsForm({
                    ...newsForm,
                    link,
                    pdf: link ? null : newsForm.pdf,
                    clear_pdf: link ? Boolean(editingNews?.pdf) : false,
                  });
                }}
              />
              <Button component="label" variant="outlined" style={{ marginTop: 16, marginRight: 8 }} disabled={Boolean(newsForm.link)}>
                PDF auswählen (max. 10 MB)
                <input
                  hidden
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(event) => setNewsForm({
                    ...newsForm,
                    pdf: event.target.files[0] || null,
                    link: '',
                    clear_pdf: false,
                  })}
                />
              </Button>
              {newsForm.pdf && <div style={{ marginTop: 8 }}>{newsForm.pdf.name}</div>}
              {!newsForm.pdf && editingNews?.pdf && !newsForm.clear_pdf && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Vorhandenes PDF bleibt erhalten.</span>
                  <Button type="button" size="small" onClick={() => setNewsForm({ ...newsForm, clear_pdf: true, link: '' })}>
                    PDF entfernen
                  </Button>
                </div>
              )}
              {newsForm.clear_pdf && <div style={{ marginTop: 8 }}>Das vorhandene PDF wird beim Speichern entfernt.</div>}
              <Alert severity="info" style={{ marginTop: 12 }}>
                Entweder einen externen Link eintragen oder ein PDF hochladen. Bei einem PDF lädt „Mehr erfahren“ die Datei herunter.
              </Alert>
              <Button component="label" variant="outlined" style={{ marginTop: 16 }}>
                Bild auswählen (JPG, PNG, WebP oder GIF; max. 5 MB)
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(event) => setNewsForm({ ...newsForm, image: event.target.files[0] || null })}
                />
              </Button>
              {newsForm.image && <div style={{ marginTop: 8 }}>{newsForm.image.name}</div>}
              {!newsForm.image && editingNews?.image && <div style={{ marginTop: 8 }}>Vorhandenes Bild bleibt erhalten, wenn kein neues gewählt wird.</div>}
            </DialogContent>
            <DialogActions className="admin-dialog-actions">
              <Button type="button" onClick={handleCloseNews} className="admin-cancel-button">Abbrechen</Button>
              <Button type="submit" disabled={saveNewsMutation.isPending} variant="contained" className="admin-save-button">
                {saveNewsMutation.isPending ? <CircularProgress size={20} /> : 'Speichern'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </>}

      {isAdmin && section === 'users' && <>
        {usersError && <Alert severity="error">{usersError.message}</Alert>}
        {saveUserMutation.isError && <Alert severity="error">{saveUserMutation.error.message}</Alert>}
        {deleteUserMutation.isError && <Alert severity="error">{deleteUserMutation.error.message}</Alert>}
        {assignUserRoleMutation.isError && <Alert severity="error">{assignUserRoleMutation.error.message}</Alert>}
        <Button variant="contained" startIcon={<Add />} onClick={() => setUserDialogOpen(true)} className="admin-add-button">
          Benutzer anlegen
        </Button>
        {isUsersLoading ? <CircularProgress /> : (
          <TableContainer component={Paper} className="admin-table-container">
            <Table>
              <TableHead className="admin-table-head">
                <TableRow><TableCell>Benutzername</TableCell><TableCell>Rolle</TableCell><TableCell>Typ</TableCell><TableCell>Aktionen</TableCell></TableRow>
              </TableHead>
              <TableBody className="admin-table-body">
                {adminUsers.map((adminUser) => (
                  <TableRow key={adminUser.id}>
                    <TableCell>{adminUser.username}</TableCell>
                    <TableCell>
                      {adminUser.is_admin ? 'Alle Berechtigungen' : (
                        <FormControl size="small" style={{ minWidth: 180 }}>
                          <Select
                            value={adminUser.role_id ?? ''}
                            displayEmpty
                            disabled={assignUserRoleMutation.isPending}
                            onChange={(event) => assignUserRoleMutation.mutate({
                              id: adminUser.id,
                              role_id: event.target.value,
                            })}
                            inputProps={{ 'aria-label': `Rolle für ${adminUser.username}` }}
                          >
                            <MenuItem value="">Keine Rolle</MenuItem>
                            {roles.map((role) => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell>{adminUser.is_admin ? 'Administrator' : 'Benutzer'}</TableCell>
                    <TableCell>
                      {!adminUser.is_admin && (
                        <IconButton onClick={() => deleteUserMutation.mutate(adminUser.id)} aria-label="Benutzer löschen" className="admin-delete-icon">
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} className="admin-dialog" fullWidth>
          <form onSubmit={handleSubmitUser}>
            <DialogTitle className="admin-dialog-title">Benutzer anlegen</DialogTitle>
            <DialogContent className="admin-dialog-content">
              <TextField required autoFocus margin="dense" label="Benutzername" fullWidth inputProps={{ minLength: 3, maxLength: 64 }} value={userForm.username} onChange={(event) => setUserForm({ ...userForm, username: event.target.value })} />
              <TextField required margin="dense" label="Passwort (mindestens 12 Zeichen)" type="password" fullWidth inputProps={{ minLength: 12 }} value={userForm.password} onChange={(event) => setUserForm({ ...userForm, password: event.target.value })} />
              <FormControl fullWidth margin="dense">
                <InputLabel id="user-role-label">Rolle</InputLabel>
                <Select labelId="user-role-label" label="Rolle" value={userForm.role_id} onChange={(event) => setUserForm({ ...userForm, role_id: event.target.value })}>
                  <MenuItem value="">Keine Rolle</MenuItem>
                  {roles.map((role) => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions className="admin-dialog-actions">
              <Button type="button" onClick={() => setUserDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit" disabled={saveUserMutation.isPending} variant="contained">{saveUserMutation.isPending ? <CircularProgress size={20} /> : 'Anlegen'}</Button>
            </DialogActions>
          </form>
        </Dialog>
      </>}

      {isAdmin && section === 'roles' && <>
        {rolesError && <Alert severity="error">{rolesError.message}</Alert>}
        {saveRoleMutation.isError && <Alert severity="error">{saveRoleMutation.error.message}</Alert>}
        {deleteRoleMutation.isError && <Alert severity="error">{deleteRoleMutation.error.message}</Alert>}
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenRole()} className="admin-add-button">Rolle anlegen</Button>
        {isRolesLoading ? <CircularProgress /> : (
          <TableContainer component={Paper} className="admin-table-container">
            <Table>
              <TableHead className="admin-table-head"><TableRow><TableCell>Rolle</TableCell><TableCell>Berechtigungen</TableCell><TableCell>Aktionen</TableCell></TableRow></TableHead>
              <TableBody className="admin-table-body">
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>{role.permissions.map((permission) => permission === 'manage_news' ? 'News' : 'Trainingszeiten').join(', ') || 'Keine'}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenRole(role)} aria-label="Rolle bearbeiten" className="admin-edit-icon"><Edit /></IconButton>
                      <IconButton onClick={() => deleteRoleMutation.mutate(role.id)} aria-label="Rolle löschen" className="admin-delete-icon"><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} className="admin-dialog" fullWidth>
          <form onSubmit={handleSubmitRole}>
            <DialogTitle className="admin-dialog-title">{editingRole ? 'Rolle bearbeiten' : 'Rolle anlegen'}</DialogTitle>
            <DialogContent className="admin-dialog-content">
              <TextField required autoFocus margin="dense" label="Rollenname" fullWidth inputProps={{ maxLength: 100 }} value={roleForm.name} onChange={(event) => setRoleForm({ ...roleForm, name: event.target.value })} />
              <FormControlLabel control={<Checkbox checked={roleForm.permissions.includes('manage_news')} onChange={(event) => updateRolePermission('manage_news', event.target.checked)} />} label="News erstellen, bearbeiten, löschen und sortieren" />
              <FormControlLabel control={<Checkbox checked={roleForm.permissions.includes('manage_trainings')} onChange={(event) => updateRolePermission('manage_trainings', event.target.checked)} />} label="Trainingszeiten bearbeiten" />
              <Alert severity="info">Benutzer- und Rollenverwaltung bleibt Administratoren vorbehalten. Administratoren haben immer alle Berechtigungen.</Alert>
            </DialogContent>
            <DialogActions className="admin-dialog-actions">
              <Button type="button" onClick={() => setRoleDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit" disabled={saveRoleMutation.isPending} variant="contained">{saveRoleMutation.isPending ? <CircularProgress size={20} /> : 'Speichern'}</Button>
            </DialogActions>
          </form>
        </Dialog>
      </>}
    </div>
  );
};
